import { AgentTask, AgentTaskStatus } from "@forgepoint/shared";

/**
 * Checks whether a task is owned exclusively by the agent (no human assignees).
 * @param task - The task to check
 * @returns True if all owners are agents
 */
export function isAgentOwned(task: AgentTask): boolean {
  if (task.owners.length === 0) {
    return false;
  }
  return task.owners.every((owner) => owner.type === "agent");
}

/** Returns whether a current task is waiting on an explicitly assigned human. */
export function isAwaitingHumanAction(task: AgentTask): boolean {
  return (
    task.status === AgentTaskStatus.IN_PROGRESS &&
    task.owners.some((owner) => owner.type === "userId")
  );
}

/**
 * Sort priority: user-actionable tasks first (awaiting approval),
 * then agent in-progress, then pending, then completed.
 * @param task - The task to compute priority for
 * @returns Numeric priority (lower = higher priority)
 */
export function getTaskSortPriority(task: AgentTask): number {
  if (isAwaitingHumanAction(task)) {
    return 0;
  }
  if (task.status === AgentTaskStatus.IN_PROGRESS) {
    return 1;
  }
  if (task.status === AgentTaskStatus.PENDING) {
    return 2;
  }
  if (task.status === AgentTaskStatus.COMPLETED) {
    return 3;
  }
  return 4;
}

/**
 * Returns a display label for the human (non-agent) owners of a task.
 * Used for "Awaiting action from <user>" task labels.
 * @param owners - The task's hydrated owner list
 * @returns A formatted string of human owner names, or null if none
 */
export function getHumanOwnerLabel(owners: AgentTask["owners"]): string | null {
  const humanOwners = owners.filter((o) => o.type === "userId");
  if (humanOwners.length === 0) {
    return null;
  }
  const labels = humanOwners
    .map((owner) => {
      if (owner.type !== "userId") {
        return null;
      }
      const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
      return name || owner.email || null;
    })
    .filter((label): label is string => label !== null);
  if (labels.length === 0) {
    return null;
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

/**
 * Formats the owners of a task into a display label.
 * @param owners - The task's hydrated owner list
 * @param agentName - The display name for agent owners
 * @returns A formatted string of owner names, or null if no owners
 */
export function formatOwnersLabel(
  owners: AgentTask["owners"],
  agentName: string,
): string | null {
  if (owners.length === 0) {
    return null;
  }
  const labels = owners
    .map((owner) => {
      if (owner.type === "agent") {
        return agentName;
      }
      const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
      return name || owner.email || null;
    })
    .filter((label): label is string => label !== null);
  if (labels.length === 0) {
    return null;
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}
